import { motion } from "framer-motion";
import { Check, ArrowRight, Truck, CalendarClock } from "lucide-react";
import { useLang } from "@/lang";
import { Reveal, Eyebrow } from "@/components/Reveal";

const Hardware = () => {
  const { t, scrollTo } = useLang();
  const h = t.hardware;

  return (
    <section id="materiel" data-testid="hardware-section" className="bg-bei-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid overflow-hidden rounded-3xl shadow-[0_30px_90px_rgba(11,25,44,0.15)] lg:grid-cols-2">
            <div className="relative bg-bei-navy p-8 sm:p-12 lg:p-14">
              <div className="grid-overlay absolute inset-0" />
              <div className="relative">
                <Eyebrow>{h.eyebrow}</Eyebrow>
                <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                  {h.title}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-300">{h.subtitle}</p>
                <ul className="mt-8 space-y-3.5">
                  {h.bullets.map((b, i) => (
                    <motion.li
                      key={i}
                      data-testid={`hardware-bullet-${i}`}
                      initial={{ opacity: 0, x: -14 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + i * 0.07, duration: 0.5 }}
                      className="flex items-start gap-3 text-sm text-slate-200 sm:text-base"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bei-cyan/20">
                        <Check className="h-3 w-3 text-bei-cyan" />
                      </span>
                      {b}
                    </motion.li>
                  ))}
                </ul>
                <button
                  data-testid="hardware-cta-quote"
                  onClick={() => scrollTo("#contact")}
                  className="group mt-10 inline-flex items-center gap-3 rounded-full bg-bei-brand px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-bei-azure hover:shadow-[0_12px_40px_rgba(0,82,204,0.45)]"
                >
                  {h.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            <div className="relative min-h-[320px] lg:min-h-0">
              <img src={h.img} alt={h.title} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-bei-navy/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex gap-3 sm:gap-4">
                <motion.div
                  data-testid="hardware-highlight-delivery"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="flex flex-1 items-center gap-3 rounded-2xl border border-white/15 bg-bei-navy/80 px-4 py-3.5 backdrop-blur-md"
                >
                  <Truck className="h-5 w-5 shrink-0 text-bei-cyan" />
                  <div>
                    <p className="font-display text-sm font-bold text-white sm:text-base">{h.hl1Value}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-300 sm:text-xs">{h.hl1Label}</p>
                  </div>
                </motion.div>
                <motion.div
                  data-testid="hardware-highlight-leasing"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.45, duration: 0.6 }}
                  className="flex flex-1 items-center gap-3 rounded-2xl border border-white/15 bg-bei-navy/80 px-4 py-3.5 backdrop-blur-md"
                >
                  <CalendarClock className="h-5 w-5 shrink-0 text-bei-cyan" />
                  <div>
                    <p className="font-display text-sm font-bold text-white sm:text-base">{h.hl2Value}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-300 sm:text-xs">{h.hl2Label}</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Hardware;
